import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Loader2Icon, PlayIcon, PlusIcon, PencilIcon, TrashIcon, HistoryIcon, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import ApiService from '../services/api.service';
import BackgroundService from '../services/bg.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTenantUsers } from '../hooks/useTenantUsers';
import { Checkbox } from '../components/ui/checkbox';
import { FeatureFlag, useFeatureFlag } from '@/services/featureFlag.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import AgentTeams from './agents/AgentTeams';

interface Agent {
  id: string;
  name: string;
  prompt: string;
  schedule: {
    cron_expression?: string;
    timezone?: string;
    enabled: boolean;
  } | null;
  notification_user_ids: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface RunningJob {
  agentId: string;
  jobId: string;
  status: 'queued' | 'started' | 'completed' | 'failed';
  threadId?: string;
}

interface AgentExecution {
  id: string;
  agent_id: string;
  triggered_by: string;
  started_at: string;
  completed_at: string | null;
  result: string;
  error_message: string | null;
  thread_id: string | null;
  artifact_id: string | null;
}

export default function Agents() {
  // agents-v2: the agent platform's agents (teams), with the Agents section's tree (AgentsLayout)
  // reaching each agent and connection. Off: the scheduled Ellie agents.
  const isV2 = useFeatureFlag(FeatureFlag.AGENTS_V2, false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [runningJobs, setRunningJobs] = useState<Map<string, RunningJob>>(new Map());
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isExecutionsSheetOpen, setIsExecutionsSheetOpen] = useState(false);
  const [executions, setExecutions] = useState<AgentExecution[]>([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    schedule: {
      cron_expression: '0 2 * * 1',
      timezone: 'America/New_York',
      enabled: false,
    },
    notification_user_ids: [] as string[],
  });
  const { data: tenantUsers } = useTenantUsers();

  // Not before sign-in is ready: an early request is a 401 and a "Failed to load" toast.
  const apiReady = useApiAuthReady();
  useEffect(() => {
    if (apiReady && !isV2) loadAgents();
  }, [apiReady, isV2]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getInstance().request('GET', '/agents') as { agents: Agent[] };
      setAgents(response.agents);
    } catch (error) {
      console.error('Failed to load agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAgent(null);
    setFormData({
      name: '',
      prompt: '',
      schedule: {
        cron_expression: '0 2 * * 1',
        timezone: 'America/New_York',
        enabled: false,
      },
      notification_user_ids: [],
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      prompt: agent.prompt,
      schedule: {
        cron_expression: agent.schedule?.cron_expression || '0 2 * * 1',
        timezone: agent.schedule?.timezone || 'America/New_York',
        enabled: agent.schedule?.enabled || false,
      },
      notification_user_ids: agent.notification_user_ids || [],
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingAgent) {
        await ApiService.getInstance().request('PATCH', `/agents/${editingAgent.id}`, formData);
        toast.success('Agent updated successfully');
      } else {
        await ApiService.getInstance().request('POST', '/agents', formData);
        toast.success('Agent created successfully');
      }
      setIsDialogOpen(false);
      loadAgents();
    } catch (error) {
      console.error('Failed to save agent:', error);
      toast.error('Failed to save agent');
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this agent?')) {
      return;
    }

    try {
      await ApiService.getInstance().request('DELETE', `/agents/${agentId}`);
      toast.success('Agent deleted successfully');
      loadAgents();
    } catch (error) {
      console.error('Failed to delete agent:', error);
      toast.error('Failed to delete agent');
    }
  };

  const handleRunNow = async (agent: Agent) => {
    try {
      const response = await ApiService.getInstance().request('POST', `/agents/${agent.id}/run`) as {
        job_id: string;
        agent_id: string;
        status: string;
      };

      const runningJob: RunningJob = {
        agentId: agent.id,
        jobId: response.job_id,
        status: 'queued',
      };
      setRunningJobs(new Map(runningJobs.set(agent.id, runningJob)));

      toast.success('Agent run started');

      const bgService = BackgroundService.getInstance();
      bgService.waitForJob(
        response.job_id,
        false,
        (progress) => {
          setRunningJobs((prev) => {
            const newMap = new Map(prev);
            const job = newMap.get(agent.id);
            if (job) {
              job.status = progress.status as any;
              newMap.set(agent.id, job);
            }
            return newMap;
          });
        }
      ).then((result: any) => {
        if (result && result.thread_id) {
          setRunningJobs((prev) => {
            const newMap = new Map(prev);
            const job = newMap.get(agent.id);
            if (job) {
              job.status = 'completed';
              job.threadId = result.thread_id;
              newMap.set(agent.id, job);
            }
            return newMap;
          });
          toast.success('Agent run completed! Click to view conversation.');
        }
      }).catch((error) => {
        console.error('Job failed:', error);
        setRunningJobs((prev) => {
          const newMap = new Map(prev);
          const job = newMap.get(agent.id);
          if (job) {
            job.status = 'failed';
            newMap.set(agent.id, job);
          }
          return newMap;
        });
        toast.error('Agent run failed');
      });
    } catch (error) {
      console.error('Failed to run agent:', error);
      toast.error('Failed to start agent run');
    }
  };

  const handleViewConversation = (threadId: string) => {
    navigate(`/ellie/conversation/${threadId}`);
  };

  const getJobStatus = (agentId: string): RunningJob | undefined => {
    return runningJobs.get(agentId);
  };

  const handleViewExecutions = async (agent: Agent) => {
    setSelectedAgent(agent);
    setIsExecutionsSheetOpen(true);
    await loadExecutions(agent.id);
  };

  const loadExecutions = async (agentId: string) => {
    try {
      setLoadingExecutions(true);
      const response = await ApiService.getInstance().request('GET', `/agents/${agentId}/executions`) as { executions: AgentExecution[] };
      setExecutions(response.executions);
    } catch (error) {
      console.error('Failed to load executions:', error);
      toast.error('Failed to load executions');
    } finally {
      setLoadingExecutions(false);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // v2 never loads the scheduled agents, so `loading` never clears there.
  if (loading && !isV2) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2Icon className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const scheduled = (
    <>
      {agents.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">No agents yet. Create your first agent to get started.</p>
          <Button onClick={handleCreate}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Agent
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => {
                const job = getJobStatus(agent.id);
                return (
                  <TableRow key={agent.id}>
                    <TableCell className="font-medium">{agent.name}</TableCell>
                    <TableCell>
                      {agent.schedule?.enabled ? (
                        <span className="text-sm text-muted-foreground">
                          {agent.schedule.cron_expression}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not scheduled</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewExecutions(agent)}
                        >
                          <HistoryIcon className="h-4 w-4 mr-1" />
                          Executions
                        </Button>
                        {job?.status === 'completed' && job.threadId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewConversation(job.threadId!)}
                          >
                            View
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRunNow(agent)}
                          disabled={job?.status === 'queued' || job?.status === 'started'}
                        >
                          {job?.status === 'queued' || job?.status === 'started' ? (
                            <Loader2Icon className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <PlayIcon className="h-4 w-4 mr-1" />
                              Run Now
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(agent)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(agent.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 page-header-surface flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-foreground">Agents</h1>
          </div>

          <p className="text-sm text-muted-foreground">
            {isV2
              ? 'Agents that work on events (alerts, Slack messages) or on a schedule, and the connections they work through.'
              : 'Create AI agents that analyze your data and generate artifacts on a schedule.'}
          </p>
        </div>
        {!isV2 && (
          <Button onClick={handleCreate}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Agent
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-8">
      {isV2 ? <AgentTeams /> : scheduled}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
            <DialogDescription>
              Configure your AI agent. The prompt tells the agent what to analyze and will also be appended to the system prompt.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Agent"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt</Label>
              <Textarea
                id="prompt"
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                placeholder="Tell the agent what to analyze..."
                rows={6}
              />
            </div>

            <div className="space-y-3 border-t pt-4">
              <Label>Notification Recipients</Label>
              <p className="text-sm text-muted-foreground">
                These users will be notified when the agent completes, and the resulting artifact will be shared with them.
              </p>
              <div className="space-y-2 max-h-48 overflow-auto">
                {(tenantUsers || []).map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 rounded-md p-2 hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={formData.notification_user_ids.includes(u.id)}
                      onCheckedChange={(checked) => {
                        setFormData({
                          ...formData,
                          notification_user_ids: checked
                            ? [...formData.notification_user_ids, u.id]
                            : formData.notification_user_ids.filter((id) => id !== u.id),
                        });
                      }}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                      {u.name && <p className="text-xs text-muted-foreground truncate">{u.email}</p>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="schedule_enabled"
                  checked={formData.schedule.enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, enabled: e.target.checked },
                    })
                  }
                  className="rounded border-gray-300"
                />
                <Label htmlFor="schedule_enabled">Enable Schedule</Label>
              </div>

              {formData.schedule.enabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="cron_expression">Cron Expression</Label>
                    <Input
                      id="cron_expression"
                      value={formData.schedule.cron_expression}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          schedule: { ...formData.schedule, cron_expression: e.target.value },
                        })
                      }
                      placeholder="0 2 * * 1"
                    />
                    <p className="text-sm text-muted-foreground">
                      Cron expression (e.g., "0 2 * * 1" for 2am every Monday)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Input
                      id="timezone"
                      value={formData.schedule.timezone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          schedule: { ...formData.schedule, timezone: e.target.value },
                        })
                      }
                      placeholder="America/New_York"
                    />
                  </div>
                </>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={isExecutionsSheetOpen} onOpenChange={setIsExecutionsSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>
              {selectedAgent ? `${selectedAgent.name} - Executions` : 'Agent Executions'}
            </SheetTitle>
            <SheetDescription>
              View all executions for this agent
            </SheetDescription>
          </SheetHeader>
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => selectedAgent && loadExecutions(selectedAgent.id)}
              disabled={loadingExecutions}
            >
              {loadingExecutions ? (
                <Loader2Icon className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
          </div>
          <div className="mt-3">
            {loadingExecutions ? (
              <div className="flex items-center justify-center py-12">
                <Loader2Icon className="h-8 w-8 animate-spin" />
              </div>
            ) : executions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No executions yet. Run the agent to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {executions.map((execution) => (
                  <div
                    key={execution.id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${execution.result === 'success' && execution.artifact_id ? 'hover:bg-accent cursor-pointer' : ''}`}
                    onClick={() => {
                      if (execution.result === 'success' && execution.artifact_id) {
                        setIsExecutionsSheetOpen(false);
                        navigate(`/artifacts/${execution.result === 'success' && execution.artifact_id}`);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {execution.result === 'success' ? (
                        <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
                      ) : execution.result === 'failure' ? (
                        <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                      ) : (
                        <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <div>
                        <div className="text-sm font-medium">
                          {execution.result === 'success' ? 'Completed' : execution.result === 'failure' ? 'Failed' : 'Running'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTimeAgo(execution.started_at)}
                          {execution.error_message && (
                            <span className="text-destructive ml-2">— {execution.error_message}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {execution.result === 'success' && execution.artifact_id && (
                      <Button variant="ghost" size="sm">
                        View Artifact
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
